<template>
  <v-dialog
    fullscreen
    eager
    v-model="dialog"
    hide-overlay
    content-class="form-dialog"
  >
    <v-card elevation="0">
      <v-col class="text-right px-4">
        <v-btn icon @click="closeDialog" ><v-icon large color="primary">close</v-icon></v-btn>
      </v-col>

      <v-container v-if="isAdmin">
        <v-form ref="form">
          <v-row>
            <v-col cols="12">
              <h2 class="primary--text font-weight-bold pt-5 pl-2">
                {{
                  this.edit ? $t('display/elevator:edit.titles.edit') + ' \'' + this.elevator.name + '\' - ' + this.elevator.id : $t('display/elevator:edit.titles.add')
                }}
              </h2>

              <BaseButton v-if="facilityId"
                          class="white--text"
                          color="primary"
                          :btnText="$t('common:goto-liftbook')"
                          icon="monitor"
                          @clickedButton="$router.push(`/${$store.state.manager.bookModuleUrl}/facilities/edit/${facilityId}`)"
              />

              <v-tabs
                color="primary"
                centered
                ref="tabs"
                v-model="currentTab"
                :show-arrows="true"
              >

                <v-tab to="#general">
                  <v-icon class="mr-3">home</v-icon>
                  {{ $t('common:general') }}
                </v-tab>

                <v-tab to="#hardware">
                  <v-icon class="mr-3">hardware</v-icon>
                  {{ $t('display/elevator:edit.tabs-title.hardware') }}
                </v-tab>

                <v-tab to="#image">
                  <v-icon class="mr-3">image</v-icon>
                  {{ $t('common:image') }}
                </v-tab>

                <v-tab to="#camera" v-if="edit">
                  <v-icon class="mr-3">photo_camera</v-icon>
                  {{ $t('display/elevator:edit.tabs-title.camera') }}
                </v-tab>

                <!-- Only for edit mode -->
                <v-tab to="#configuration" v-if="edit">
                  <v-icon class="mr-3">router</v-icon>
                  {{ $t('display/elevator:edit.tabs-title.configuration') }}
                </v-tab>

                <v-tab to="#comments" v-if="edit">
                  <v-icon class="mr-3">comment_bank</v-icon>
                  {{ $t('common:comment_plural') }}
                </v-tab>

                <v-tab to="#playlist" v-if="edit">
                  <v-icon class="mr-3">featured_play_list</v-icon>
                  {{ $t('display/elevator:edit.tabs-title.playlist') }}
                </v-tab>

              </v-tabs>
              <v-tabs-items v-model="currentTab">
                <v-tab-item value="general" eager>
                  <DataTab/>
                </v-tab-item>
                <v-tab-item value="hardware" eager>
                  <HardwareTab/>
                </v-tab-item>
                <v-tab-item value="image" eager>
                  <ImageTab/>
                </v-tab-item>
                <v-tab-item value="camera" v-if="edit" eager>
                  <CameraTab ref="cameraTab" />
                </v-tab-item>
                <v-tab-item v-if="edit" value="configuration" eager>
                  <h3 class="primary--text font-weight-bold pt-5 pl-2">
                    {{ $t('display/elevator:edit.tabs-title.server-configuration') }}
                  </h3>
                  <ServerOptionForm
                      :start-options="$store.state.display.elevatorForm.elevator.options"
                      @updateOptions="updateOptions"
                  />

                  <h3 class="primary--text font-weight-bold pt-5 pl-2">
                    {{ $t('display/elevator:edit.tabs-title.client-configuration') }}
                  </h3>
                  <ClientConfigurationTab />

                  <h3 class="primary--text font-weight-bold pt-5 pl-2">
                    {{ $t('display/elevator:edit.tabs-title.vast') }}
                  </h3>
                  <VastTab />
                </v-tab-item>
                <!-- Only for edit mode -->
                <v-tab-item v-if="edit" value="comments" eager>
                  <CommentsTab/>
                </v-tab-item>
                <v-tab-item v-if="edit" value="playlist" eager>
                  <PlaylistTab />
                </v-tab-item>
              </v-tabs-items>
            </v-col>
            <v-col cols="12">
              <v-card
                elevation="0"
                flat
                tile
                class="pl-3 pt-3 pb-md-3"
              >
                <TwoButtonDialog
                  ref="saveButton"
                  :btn-text="$t('buttons:save')"
                  color="primary"
                  :hideOverlay="true"
                  :dialog-title="$t('dialog:save-btn-title', {name : $t('display/elevator:edit.dialog.dialog-part-1')})"
                  :dialog-text="$t('dialog:save-btn-dialog', {name : $t('display/elevator:edit.dialog.dialog-part-2')})"
                  :button1-text="$t('buttons:save')"
                  :button2-text="$t('buttons:cancel')"
                  @clickedButton1="updateForm()"
                  @clickedButton2="$refs.saveButton.showDialog = false"
                />
                <DirtyConfirmButtonDialog
                  ref="dirtyDialog"
                  :btn-text="$t('buttons:back')"
                  color="grey"
                  :hideOverlay="true"
                  :dialog-title="$t('dialog:dirty-save-btn-title')"
                  :dialog-text="$t('dialog:dirty-save-btn-dialog')"
                  :button1-text="$t('dialog:dirty-save-btn-1-text')"
                  :button2-text="$t('dialog:dirty-save-btn-2-text')"
                  @clickedButton="closeDialog"
                />
              </v-card>
            </v-col>
          </v-row>
        </v-form>
      </v-container>
      <v-container v-else>
        <v-form ref="form">
          <v-row>
            <v-col cols="12">
              <h2 class="primary--text font-weight-bold pt-5 pl-2">
                {{ this.edit ? $t('display/elevator:edit.titles.edit') + ' \'' + this.elevator.name + '\'' : '' }}
              </h2>

              <BaseButton v-if="facilityId"
                  class="white--text"
                  color="primary"
                  :btnText="$t('common:goto-liftbook')"
                  icon="monitor"
                  @clickedButton="$router.push(`/${$store.state.manager.bookModuleUrl}/facilities/edit/${facilityId}`)"
              />

              <ImageTab/>
            </v-col>
            <v-col cols="12">
              <AdditionalElevator
                  @updateElevators="e => $store.commit('display/elevatorForm/updateAdditionalElevators', e)"
                  ref="additionalElevators"
              />
            </v-col>
            <v-col cols="12">
              <v-card
                elevation="0"
                flat
                tile
                class="pl-3 pt-3 pb-md-3"
              >
                <TwoButtonDialog
                  ref="saveButton"
                  :btn-text="$t('buttons:save')"
                  color="primary"
                  :hideOverlay="true"
                  :dialog-title="$t('dialog:save-btn-title', {name : $t('display/elevator:edit.dialog.dialog-part-1')})"
                  :dialog-text="$t('dialog:save-btn-dialog', {name : $t('display/elevator:edit.dialog.dialog-part-2')})"
                  :button1-text="$t('buttons:save')"
                  :button2-text="$t('buttons:cancel')"
                  @clickedButton1="updateForm()"
                  @clickedButton2="$refs.saveButton.showDialog = false"
                />
                <DirtyConfirmButtonDialog
                  ref="dirtyDialog"
                  :btn-text="$t('buttons:back')"
                  color="grey"
                  :hideOverlay="true"
                  :dialog-title="$t('dialog:dirty-save-btn-title')"
                  :dialog-text="$t('dialog:dirty-save-btn-dialog')"
                  :button1-text="$t('dialog:dirty-save-btn-1-text')"
                  :button2-text="$t('dialog:dirty-save-btn-2-text')"
                  @clickedButton="closeDialog"
                />
              </v-card>
            </v-col>
          </v-row>
        </v-form>
      </v-container>
    </v-card>
  </v-dialog>
</template>

<script>

import DataTab from '@/modules/display/views/elevator/form/DataTab';
import HardwareTab from '@/modules/display/views/elevator/form/HardwareTab';
import ServerOptionForm from '@/modules/core/components/ServerOptionForm';
import ClientConfigurationTab from '@/modules/display/views/elevator/form/ClientConfigurationTab';
import VastTab from '@/modules/display/views/elevator/form/VastTab';
import ImageTab from '@/modules/display/views/elevator/form/ImageTab';
import CameraTab from '@/modules/display/views/elevator/form/CameraTab';
import PlaylistTab from '@/modules/display/views/elevator/form/PlaylistTab';
import CommentsTab from '@/modules/display/views/elevator/form/CommentsTab';
import AdditionalElevator from '@/modules/display/components/AdditionalElevator';
import ElevatorService from '@/modules/display/services/elevator-service';
import TwoButtonDialog from '@/modules/core/components/TwoButtonDialog/TwoButtonDialog';
import DirtyConfirmButtonDialog from '@/modules/core/components/TwoButtonDialog/DirtyConfirmButtonDialog';
import Utils from '@/modules/core/services/utils';
import FacilityService from "@/modules/book/services/facility-service";
import BaseButton from "@/modules/core/components/Base/BaseButton";

export default {
  name: "ElevatorForm",
  components: {
    DataTab,
    TwoButtonDialog,
    HardwareTab,
    ServerOptionForm,
    ClientConfigurationTab,
    DirtyConfirmButtonDialog,
    ImageTab,
    CommentsTab,
    AdditionalElevator,
    PlaylistTab,
    VastTab,
    CameraTab,
    BaseButton
  },

  data() {
    return {
      dialog: false,
      edit: false,
      currentTab: null,
      loadData: false,
      elevator: null,
      elevatorBeforeChange: null,
      itemReference: null,
      facilityId: null,
    }
  },

  computed: {
    isAdmin() {
      return this.$store.getters["auth/isAdmin"];
    },
    isManager() {
      return this.$store.getters["auth/isManager"] && !this.$store.getters["auth/isOperator"];
    },
    isOperator() {
      return this.$store.getters["auth/isOperator"];
    }
  },

  methods: {
    updateForm() {
      if (!this.$refs.form.validate()) {
        Utils.showFailureSnackbar(this.$t('snackbar:empty-fields'));
        this.$refs.saveButton.showDialog = false;
        return;
      }

      let elevator = Object.assign({}, this.$store.state.display.elevatorForm.elevator);

      if (this.$store.state.display.elevatorForm.elevator.additionalElevators) {
        elevator.additionalElevators = this.$store.state.display.elevatorForm.elevator.additionalElevators;
      }

      if (elevator.id != null) {
        ElevatorService.putToAPI(
          elevator,
          () => {
            Utils.showSuccessSnackbar(this.$t('snackbar:edit-success', {name: this.$t('common:elevator')}));
            this.$refs.saveButton.showDialog = false;
            this.closeDialog();
            this.closeForm();
          },
          () => {
            Utils.showFailureSnackbar(this.$t('snackbar:edit-failure', {name: this.$t('common:elevator')}));
            this.closeDialog();
          }
        )
      } else {
        ElevatorService.postToAPI(
          elevator,
          () => {
            Utils.showSuccessSnackbar(this.$t('snackbar:add-success', {name: this.$t('common:elevator')}));
            this.$refs.saveButton.showDialog = false;
            this.closeForm();
          },
          (error) => {
            this.$logger.error("Error at elevator saving: " + error);
            Utils.showFailureSnackbar(this.$t('add-failure', {name: this.$t('common:elevator')}));
            this.closeDialog();
            this.$emit('updateTable');
          }
        )
      }
    },

    resetData() {
      this.edit = false;
      this.loadData = false;
      this.elevator = null;
      this.elevatorBeforeChange = null;
      this.$store.commit('display/elevatorForm/resetForm');
      this.$refs.form.reset();
      this.itemReference = null;
      this.facilityId = null;
    },

    isDataChanged() {
      if (this.elevator === null || this.elevatorBeforeChange === null) {
        return false;
      }

      let actualElevator = Object.assign({}, this.elevator);
      let elevatorBeforeChange = Object.assign({}, this.elevatorBeforeChange);
      actualElevator.tags = [];
      actualElevator.blackTags = [];

      elevatorBeforeChange.tags = [];
      elevatorBeforeChange.blackTags = [];


      if (JSON.stringify(elevatorBeforeChange) !== JSON.stringify(actualElevator)
        || this.elevator.tags.length !== this.elevatorBeforeChange.tags.length
        || this.elevator.blackTags.length !== this.elevator.blackTags.length
      ) {
        return true;
      }

      for (let i = 0; i < this.elevator.tags.length; i++) {
        if (this.elevatorBeforeChange.tags.indexOf(this.elevator.tags[i].id) < 0) {
          return true;
        }
      }

      for (let i = 0; i < this.elevator.blackTags.length; i++) {
        if (this.elevatorBeforeChange.blackTags.indexOf(this.elevator.blackTags[i].id) < 0) {
          return true;
        }
      }

      return false;
    },

    readElevator(id) {
      ElevatorService.readFromAPI(
          id,
          (response) => {
            this.$store.commit('display/elevatorForm/setElevator', response.data);
            this.$store.commit('display/elevatorForm/updateForm');
            this.dialog = true;
            this.edit = true;

            this.elevator = this.$store.state.display.elevatorForm.elevator;
            this.elevatorBeforeChange = Object.assign({}, this.elevator);

            if (!this.isAdmin) {
              this.$refs.additionalElevators.elevatorLoaded(this.elevator);
            }

            this.elevatorBeforeChange.emergencyContactList
              = [].concat(this.elevator.emergencyContactList);

            if (this.isAdmin || this.isManager || this.isOperator) {
              //load facility id
              FacilityService.readFromAPIByDisplayId(
                  id,
                  (response) => {
                    if (response.data && response.data.id) {
                      this.facilityId = response.data.id;
                    }
                  },
                  (error) => {
                    this.$logger.error("Failed loading facility with display id " + id + ", because: " + error);
                    Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('common:facility')}));
                  }
              );
            }

            this.$watch('elevator',
              () => {
                this.$refs.dirtyDialog.setDirty(this.isDataChanged());
              },
              {deep: true}
            );

          }
      )
    },

    initialization(elevator) {
      this.resetData();
      this.itemReference = elevator;
      if (typeof elevator != 'undefined' && typeof elevator.id != 'undefined') {
        this.readElevator(elevator.id);
      } else {
        this.dialog = true;
        this.$store.commit('display/elevatorForm/updateForm');
        this.elevator = this.$store.state.display.elevatorForm.elevator
        this.elevatorBeforeChange = Object.assign({}, this.elevator);
        this.$watch('elevator',
          () => {
            if (JSON.stringify(this.elevatorBeforeChange) !== JSON.stringify(this.elevator)) {
              this.$refs.dirtyDialog.setDirty(true)
            }
          },
          {deep: true}
        );
      }
    },

    initializationById(id) {
      this.resetData();
      this.readElevator(id);
    },

    closeForm() {
      this.dialog = false;
      this.$emit('updateTable');
    },

    closeDialog() {
      this.dialog = false;

      if (this.$refs.cameraTab) {
        this.$refs.cameraTab.reset();
      }
    },

    /**
     * Method that gets called whenever options are getting updated by ServerOptionForm.
     *
     * @param options updated options value, contains all option entries
     */
    updateOptions(options){
      this.$store.commit("display/elevatorForm/updatedOptions", options);
    }
  },

  created() {
    this.$store.commit('display/elevatorForm/setCloseForm', this.closeForm);
  },

  watch: {
    dialog() {
      if(!this.dialog) {
        this.$store.commit('display/elevatorForm/beforeClose');
      }
    }
  }
}
</script>

<style scoped>
/*
  had to set z-index of dialog and overlay manually here
  because here we have a single case of dialog over dialog
*/
.v-dialog__content.v-dialog__content--active {
  z-index: 450 !important;
}
</style>
